import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { getRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import ActionModalHeader from "../../Users/modals/ActionModalHeader";
import { PAYMENT_MODES, affiliateName, inr } from "../components/AffiliateLayout";

export interface PayoutStatusChange {
    payout: any;   // row from /affiliate-payout-list or payout from /affiliate-payout-details
    status: number; // 0 Pending, 1 Paid, 2 Failed
}

interface PayoutStatusModalProps {
    change: PayoutStatusChange | null;
    onHide: () => void;
    onSuccess: () => void;
}

// [icon, tone, title, body, remark label, placeholder, button]
const STATUS_COPY: Record<number, [string, "ok" | "warn" | "bad", string, string, string, string, string]> = {
    1: ["ri-checkbox-circle-line", "ok", "Mark payout as paid?", "Its commissions are marked paid and the affiliate sees the payout as successful.", "Note", "e.g. Paid for September referrals", "Mark paid"],
    2: ["ri-close-circle-line", "bad", "Mark payout as failed?", "Its commissions go back to the affiliate's available balance so they can request a new payout. A failed payout can't be reopened.", "Reason", "e.g. Bank account closed — asked affiliate to update", "Mark failed"],
    0: ["ri-arrow-go-back-line", "warn", "Move payout back to pending?", "Use this only to undo a payout marked paid by mistake. Its commissions become pending again.", "Note", "e.g. Marked paid by mistake — transfer not done", "Move to pending"],
};

// Reference field label + placeholder per payment mode
const REFERENCE_COPY: Record<string, [string, string]> = {
    bank_transfer: ["UTR / transaction ID", "e.g. UTR 4152XXXX8810"],
    upi: ["UPI reference no.", "e.g. 4271XXXX0912"],
    cash: ["Receipt / voucher no.", "e.g. CASH-0142"],
    cheque: ["Cheque no.", "e.g. 004512 · HDFC"],
    other: ["Reference", "e.g. transaction ID"],
};

// Confirms a payout status change (/affiliate/changeSettlementStatus/:id/:status?paymentMode=&reference=&remark=)
const PayoutStatusModal = ({ change, onHide, onSuccess }: PayoutStatusModalProps) => {
    const [remark, setRemark] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [reference, setReference] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (change) {
            setRemark("");
            setPaymentMode("");
            setReference("");
        }
    }, [change]);

    const isPaid = change?.status === 1;

    const handleSubmit = async () => {
        if (!change) return;
        if (isPaid && !paymentMode) return toast.warning("Please select the payment mode");

        try {
            setIsSaving(true);
            const response = await getRequest(
                `/affiliate/changeSettlementStatus/${change.payout._id}/${change.status}?remark=${encodeURIComponent(remark.trim())}` +
                (isPaid ? `&paymentMode=${paymentMode}&reference=${encodeURIComponent(reference.trim())}` : "")
            );

            if (response.status === "success") {
                toast.success(response.message);
                onSuccess();
                onHide();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setIsSaving(false);
        }
    };

    const copy = change ? STATUS_COPY[change.status] : null;
    const p = change?.payout;
    // list rows carry the affiliate name flat, the details payout has it populated
    const name = p ? (p.ap_affiliate_id?.affiliate_first_name ? affiliateName(p.ap_affiliate_id) : affiliateName(p)) : "";
    const [refLabel, refPlaceholder] = REFERENCE_COPY[paymentMode] || REFERENCE_COPY.other;

    return (
        <Modal show={!!change} onHide={onHide} centered contentClassName="cx">
            {change && copy && <>
                <ActionModalHeader icon={copy[0]} tone={copy[1]} title={copy[2]} subtitle={copy[3]} onClose={onHide} />
                <div style={{ padding: "14px 20px" }}>
                    <div className="cx-kv k3" style={{ marginBottom: 14 }}>
                        <div><small>Payout</small><b className="mono">#{p.ap_unique_id || p.ap_id}</b></div>
                        <div><small>Affiliate</small><b>{name}</b></div>
                        <div><small>Amount</small><b className="mono">{inr(p.ap_amount)}</b></div>
                    </div>

                    {isPaid && <>
                        <label className="cx-label">Payment mode</label>
                        <div className="cx-chips" style={{ marginBottom: 14 }}>
                            {PAYMENT_MODES.map(([value, label, icon]) => (
                                <button type="button" key={value} className={`cx-chip ${paymentMode === value ? "on" : ""}`} onClick={() => setPaymentMode(value)}>
                                    <i className={icon}></i>{label}
                                </button>
                            ))}
                        </div>

                        <label className="cx-label">{refLabel} <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
                        <input className="cx-input mono" style={{ width: "100%", marginBottom: 14 }} placeholder={refPlaceholder} value={reference} onChange={(e) => setReference(e.target.value)} />
                    </>}

                    <label className="cx-label">{copy[4]} <span className="muted" style={{ fontWeight: 400 }}>(optional, saved in the remark)</span></label>
                    <input className="cx-input" style={{ width: "100%" }} placeholder={copy[5]} value={remark} onChange={(e) => setRemark(e.target.value)} />
                </div>
                <div style={{ padding: "0 20px 18px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button type="button" className="cx-btn cx-btn-light" onClick={onHide}>Cancel</button>
                    <button
                        type="button"
                        className={`cx-btn ${change.status === 2 ? "cx-btn-danger" : change.status === 1 ? "cx-btn-signal" : "cx-btn-primary"}`}
                        onClick={handleSubmit}
                        disabled={isSaving || (isPaid && !paymentMode)}
                    >
                        {isSaving ? "Saving…" : copy[6]}
                    </button>
                </div>
            </>}
        </Modal>
    );
};

export default PayoutStatusModal;
